(() => {
  if (window.__outlandishVideoExpandInitialized) return;
  window.__outlandishVideoExpandInitialized = true;

  const cardSelector = '[data-outlandish-video-card]';
  const buttonSelector = '[data-outlandish-video-expand]';
  const bodyClass = 'outlandish-video-is-expanded';
  const fallbackLocations = new Map();

  const getFullscreenElement = () =>
    document.fullscreenElement || document.webkitFullscreenElement || null;

  const configureIframe = (iframe) => {
    if (!iframe) return;

    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('webkitallowfullscreen', '');

    const permissions = (iframe.getAttribute('allow') || '')
      .split(';')
      .map((permission) => permission.trim())
      .filter(Boolean);

    if (!permissions.some((permission) => permission === 'fullscreen' || permission.startsWith('fullscreen '))) {
      permissions.push('fullscreen');
    }

    iframe.setAttribute('allow', permissions.join('; '));
  };

  const configureVideo = (video) => {
    if (!video) return;
    video.setAttribute('controlslist', 'nofullscreen');
  };

  const configureCard = (card) => {
    if (!card) return;
    card.querySelectorAll('video').forEach(configureVideo);
    card.querySelectorAll('iframe').forEach(configureIframe);
  };

  const setButtonState = (card, expanded) => {
    const button = card?.querySelector(buttonSelector);
    if (!button) return;

    button.setAttribute('aria-pressed', expanded ? 'true' : 'false');
    button.setAttribute('aria-label', expanded ? 'Close full-screen video' : 'View video full screen');
  };

  const restoreCard = (card) => {
    const location = fallbackLocations.get(card);
    if (!location) return;

    if (location.placeholder.isConnected) {
      location.placeholder.parentNode.insertBefore(card, location.placeholder);
      location.placeholder.remove();
    } else if (location.parent?.isConnected) {
      location.parent.appendChild(card);
    }

    fallbackLocations.delete(card);
  };

  const closeFallback = (card) => {
    if (!card) return;

    card.classList.remove('is-expanded');
    restoreCard(card);
    document.body.classList.toggle(bodyClass, fallbackLocations.size > 0);
    setButtonState(card, false);
  };

  const openFallback = (card) => {
    document.querySelectorAll(`${cardSelector}.is-expanded`).forEach(closeFallback);

    const placeholder = document.createComment('outlandish-video-location');
    const parent = card.parentNode;
    parent.insertBefore(placeholder, card);
    fallbackLocations.set(card, { parent, placeholder });
    document.body.appendChild(card);

    card.classList.add('is-expanded');
    document.body.classList.add(bodyClass);
    setButtonState(card, true);
    configureCard(card);

    const video = card.querySelector('video');
    if (video) video.controls = true;
  };

  const requestFullscreen = async (card) => {
    try {
      if (card.requestFullscreen) {
        await card.requestFullscreen();
        return getFullscreenElement() === card;
      }

      if (card.webkitRequestFullscreen) {
        card.webkitRequestFullscreen();
        return getFullscreenElement() === card;
      }
    } catch (error) {
      return false;
    }

    return false;
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    } catch (error) {
      // The fixed-position fallback remains available when native full screen is blocked.
    }
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(buttonSelector);
    if (!button) return;

    const card = button.closest(cardSelector);
    if (!card) return;

    event.preventDefault();
    event.stopPropagation();
    configureCard(card);

    if (getFullscreenElement() === card) {
      await exitFullscreen();
      return;
    }

    if (card.classList.contains('is-expanded')) {
      closeFallback(card);
      return;
    }

    const openedNatively = await requestFullscreen(card);
    if (!openedNatively) openFallback(card);
  });

  const handleFullscreenChange = () => {
    const fullscreenElement = getFullscreenElement();

    document.querySelectorAll(cardSelector).forEach((card) => {
      const isFullscreen = fullscreenElement === card;
      card.classList.toggle('is-native-fullscreen', isFullscreen);
      setButtonState(card, isFullscreen || card.classList.contains('is-expanded'));
    });
  };

  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const expandedCard = document.querySelector(`${cardSelector}.is-expanded`);
    if (expandedCard) closeFallback(expandedCard);
  });

  document.addEventListener('shopify:section:unload', () => {
    document.querySelectorAll(`${cardSelector}.is-expanded`).forEach(closeFallback);
  });

  document.querySelectorAll(cardSelector).forEach(configureCard);
})();
